import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { RouteData, LayoutServiceContext } from '@sitecore-jss/sitecore-jss/layout';
import { validateRoute } from './helpers/sitecoreRouteValidator';

export interface PageScript {
  tag: string;
  placement: string;
  innerHtml?: string;
  attributes?: Record<string, string>;
}

export interface SiteInfo {
  name?: string;
}

export const useJssStore = defineStore('jss', () => {
  const NON_PERSISTENT_STATE_DEFAULTS = {
    pubAddress: '',
  };

  const appVersion = ref('');
  const appHostName = ref('');
  const site = ref<SiteInfo>({});
  const dictionaryPrefix = ref('');
  const siteVirtualFolder = ref('');
  const language = ref('');
  const favicon = ref('');
  const timeStamp = ref('');

  const bookingAppUrl = ref('');
  const canonicalUrl = ref('');
  const consentApiUrl = ref('');
  const consentApiKey = ref('');
  const googleSiteVerification = ref('');
  const metaRobots = ref('');
  const pageScripts = ref<PageScript[]>([]);
  const schemas = ref<string[]>([]);

  // Brand & Venue properties
  const brandName = ref('');
  const segment = ref('');
  const division = ref('');
  const subDivision = ref('');
  const subSegment = ref('');
  const tenantName = ref('');
  const marketingBrandName = ref('');
  const pubAddress = ref(NON_PERSISTENT_STATE_DEFAULTS.pubAddress);
  const theme = ref('');
  const venueName = ref('');
  const venueId = ref('');
  const rawVenueId = ref('');
  const locationKey = ref('');
  const venueCountry = ref('');
  const venueLocation = ref('');
  const venueOrigin = ref('');
  const venueAppName = ref('');
  const isHotel = ref(false);
  const sports = ref(false);

  // Page state
  const pageEditing = ref(false);
  const isBookingAppPage = ref(false);
  const pageState = ref('');
  const itemPath = ref('');
  const topLevelPageCategory = ref('');

  // APIs
  const enableContentCard = ref(false);
  const brazeSdkKey = ref('');
  const woosmapApiKey = ref('');
  const guestlineUrl = ref('');

  // Route data
  const routeData = ref<RouteData | null>(null);

  const routeName = computed(() => routeData.value?.name);
  const itemId = computed(() => routeData.value?.itemId);
  const validatedRouteData = computed(() =>
    pageEditing.value ? validateRoute(routeData.value) : routeData.value
  );

  function patchState(
    this: { $patch: (state: Record<string, unknown>) => void },
    context: LayoutServiceContext,
    route: RouteData | null | undefined
  ): void {
    this.$patch({
      ...NON_PERSISTENT_STATE_DEFAULTS,
      ...context,
      routeData: route,
    });
  }

  return {
    NON_PERSISTENT_STATE_DEFAULTS,
    appHostName,
    appVersion,
    bookingAppUrl,
    brandName,
    brazeSdkKey,
    canonicalUrl,
    consentApiUrl,
    consentApiKey,
    dictionaryPrefix,
    division,
    enableContentCard,
    favicon,
    googleSiteVerification,
    guestlineUrl,
    isBookingAppPage,
    isHotel,
    itemId,
    itemPath,
    language,
    locationKey,
    marketingBrandName,
    metaRobots,
    pageEditing,
    pageScripts,
    pageState,
    pubAddress,
    rawVenueId,
    routeData,
    routeName,
    schemas,
    segment,
    site,
    siteVirtualFolder,
    sports,
    subDivision,
    subSegment,
    tenantName,
    theme,
    timeStamp,
    topLevelPageCategory,
    validatedRouteData,
    venueAppName,
    venueCountry,
    venueId,
    venueLocation,
    venueName,
    venueOrigin,
    woosmapApiKey,
    patchState,
  };
});
